import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { MotivatorCard } from './MotivatorCard';
import { Motivator } from '../types';

interface MotivatorListProps {
  motivators: Motivator[];
  ranking: string[];
  onRankingChange: (newRanking: string[]) => void;
}

export const MotivatorList = ({ motivators, ranking, onRankingChange }: MotivatorListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = ranking.indexOf(active.id as string);
      const newIndex = ranking.indexOf(over.id as string);

      const newRanking = arrayMove(ranking, oldIndex, newIndex);
      onRankingChange(newRanking);
    }
  };

  const sortedMotivators = ranking
    .map((id) => motivators.find((m) => m.id === id))
    .filter((m): m is Motivator => m !== undefined);

  return (
    <>
      {/* Desktop - Horizontal Grid */}
      <div className="hidden md:block">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ranking} strategy={horizontalListSortingStrategy}>
            <div className="grid grid-cols-5 gap-2 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10">
              {sortedMotivators.map((motivator) => (
                <MotivatorCard
                  key={motivator.id}
                  id={motivator.id}
                  name={motivator.name}
                  description={motivator.description}
                  image={motivator.image}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Mobile - Vertical Grid */}
      <div className="block md:hidden">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ranking} strategy={verticalListSortingStrategy}>
            <div className="grid grid-cols-2 gap-2">
              {sortedMotivators.map((motivator) => (
                <MotivatorCard
                  key={motivator.id}
                  id={motivator.id}
                  name={motivator.name}
                  description={motivator.description}
                  image={motivator.image}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
};
